import {
  ClearBehaviour,
  IconName,
  Select,
  Toolbar,
  ToolbarOption,
} from '@infotech-soe/erp-ui-kit';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import { OptionType } from '../../commands/CommandsToolbar';
import { DEFAULT_PRESET } from '../constants';
import { ConfigFormActionType } from '../utils/ConfigFormReducer';

export type SupportedToolbarActions =
  | ConfigFormActionType.ADD_PRESET
  | ConfigFormActionType.COPY_PRESET
  | ConfigFormActionType.DELETE_PRESET
  | ConfigFormActionType.RENAME_PRESET
  | ConfigFormActionType.RESET_PRESET
  | ConfigFormActionType.IMPORT_PRESET
  | ConfigFormActionType.EXPORT_PRESET;

interface Props {
  onAction: (action: SupportedToolbarActions) => Promise<void>;
  onChange: (name: string) => void;
  current: string;
  presets: string[];
  className?: string;
}

const messages = defineMessages({
  [ConfigFormActionType.ADD_PRESET]: {
    id: 'core.category.grid.config.preset.add',
    defaultMessage: 'Add preset',
  },
  [ConfigFormActionType.COPY_PRESET]: {
    id: 'core.category.grid.config.preset.copy',
    defaultMessage: 'Copy preset',
  },
  [ConfigFormActionType.DELETE_PRESET]: {
    id: 'core.category.grid.config.preset.delete',
    defaultMessage: 'Remove preset',
  },
  [ConfigFormActionType.RENAME_PRESET]: {
    id: 'core.category.grid.config.preset.rename',
    defaultMessage: 'Rename preset',
  },
  [ConfigFormActionType.RESET_PRESET]: {
    id: 'core.category.grid.config.preset.reset',
    defaultMessage: 'Reset preset',
  },
  [ConfigFormActionType.IMPORT_PRESET]: {
    id: 'core.category.grid.config.preset.import',
    defaultMessage: 'Import preset',
  },
  [ConfigFormActionType.EXPORT_PRESET]: {
    id: 'core.category.grid.config.preset.export',
    defaultMessage: 'Export preset',
  },
  currentPreset: {
    id: 'core.category.grid.config.preset.current',
    defaultMessage: 'Your preset',
  },
});

const TOOLBAR_ACTIONS: Array<SupportedToolbarActions | null> = [
  ConfigFormActionType.ADD_PRESET,
  ConfigFormActionType.COPY_PRESET,
  ConfigFormActionType.DELETE_PRESET,
  ConfigFormActionType.RENAME_PRESET,
  null,
  ConfigFormActionType.RESET_PRESET,
  null,
  ConfigFormActionType.IMPORT_PRESET,
  ConfigFormActionType.EXPORT_PRESET,
];

const ICON_MAP: Record<SupportedToolbarActions, IconName> = {
  [ConfigFormActionType.ADD_PRESET]: 'add',
  [ConfigFormActionType.COPY_PRESET]: 'duplicate',
  [ConfigFormActionType.DELETE_PRESET]: 'delete',
  [ConfigFormActionType.RENAME_PRESET]: 'edit',
  [ConfigFormActionType.RESET_PRESET]: 'reverse',
  [ConfigFormActionType.IMPORT_PRESET]: 'upload',
  [ConfigFormActionType.EXPORT_PRESET]: 'export',
};

const isToolbarOptionDisabled = (
  action: SupportedToolbarActions,
  condition: string,
  defaultPresetName: string
) => {
  switch (action) {
    case ConfigFormActionType.COPY_PRESET:
      return /\bcopy\b/gi.test(condition);
    case ConfigFormActionType.DELETE_PRESET:
    case ConfigFormActionType.RENAME_PRESET:
      return condition === (defaultPresetName || DEFAULT_PRESET);
    default:
      return;
  }
};

const useStyles = createUseStyles({
  addPresetButton: {
    backgroundColor: 'var(--primary)',
    color: 'var(--typoDark)',
    '&:hover': {
      backgroundColor: 'var(--primaryHover)',
      color: 'var(--typoDark)',
    },
  },
});

const ConfigToolbar: React.FC<Props> = ({
  onAction,
  onChange,
  current,
  presets = [],
  className,
}) => {
  const intl = useIntl();
  const classes = useStyles();

  const toolbarOptions: Array<ToolbarOption<SupportedToolbarActions>> = useMemo(
    () =>
      TOOLBAR_ACTIONS.map(action =>
        action === null
          ? { type: OptionType.SEPARATOR }
          : {
              icon: ICON_MAP[action],
              name: action,
              type: OptionType.BUTTON,
              label: intl.formatMessage(messages[action]),
              disabled: isToolbarOptionDisabled(action, current, presets[0]),
              ...(action === ConfigFormActionType.ADD_PRESET && {
                className: classes.addPresetButton,
              }),
            }
      ),
    [classes.addPresetButton, current, intl, presets]
  );

  return (
    <div className={`col row ${className ?? ''}`}>
      <div className="col-auto">
        <Select
          className="m-ma-xs"
          value={current}
          name="preset"
          onChange={v => onChange(v ?? '')}
          options={presets.map(key => ({
            label: key,
            value: key,
            type: 'option',
          }))}
          placeholder={intl.formatMessage(messages.currentPreset)}
          withClear={ClearBehaviour.NONE}
        />
      </div>
      <div className="col">
        <Toolbar
          onSelect={async (name: SupportedToolbarActions) => {
            await onAction(name);
          }}
          options={toolbarOptions}
        />
      </div>
    </div>
  );
};

export default ConfigToolbar;
